import { reactular } from '@vendhq/reactular'
import {
  Header,
  PageHeader,
  Section,
  Text,
} from '@lightspeed/design-system-react'
import React, { useEffect, useState, useCallback } from 'react'
import { useT } from '@transifex/react'
import orderBy from 'lodash/orderBy'
import { NoPermissionsPage } from '@vend/react-utilities'
import {
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleSyncManager,
  useUserManager,
  useVdFeatureManager,
  useVdTracking,
  useWebRegisterStatusManager,
} from '../../ng-compat/hooks'
import { Sale } from '../../models/sale'
import { WEB_REGISTER } from '../web-register.constant'
import { drop } from '../../factories/database/database'
import { useAppSelector } from '../../state/hooks'
import {
  selectActiveOutletId,
  selectActiveRegisterId,
  selectLoggedInUserId,
} from '../../state/slices/posState/posStateSlice'
import {
  FEATURE_ENABLE_REGISTER_ACCESS_CONTROL,
  PERMISSIONS,
} from '../../constants'
import { ErrorSales } from './ErrorSales'
import { ResetData } from './ResetData'

export const StatusView: React.FC = () => {
  const t = useT()
  const [erroredSales, setErroredSales] = useState<Sale[]>([])
  const [outletTimeZone, setOutletTimeZone] = useState<string>('')
  const [erroredSalesFileUrl, setErroredSalesFileUrl] = useState<string>('')
  const saleSyncManager = useSaleSyncManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const userManager = useUserManager()
  const vdFeatureManager = useVdFeatureManager()
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const registerManager = useRegisterManager()
  const vdTracking = useVdTracking()
  const registerId = useAppSelector(selectActiveRegisterId)
  const outletId = useAppSelector(selectActiveOutletId)
  // Trigger a re-render when the user changes
  useAppSelector(selectLoggedInUserId)

  const hasAccessToReset = () => {
    const registerAccessControlEnabled = vdFeatureManager.get(
      FEATURE_ENABLE_REGISTER_ACCESS_CONTROL
    )
    return registerAccessControlEnabled
      ? userManager.hasPermission(PERMISSIONS.REGISTER_STATUS_RESET)
      : true
  }

  const resetData = () => {
    vdTracking.trackUserEvent({
      eventName: 'Reset Data',
      metadata: {
        register_id: registerId,
      },
      useBatching: false,
      useSendBeacon: true,
    })

    drop()
      // Refresh the application cache and reload the browser. This effectively triggers a cold load and
      // sync as if the user is accessing Web Register for the first time in this browser
      .then(() => {
        webRegisterStatusManager.refreshCacheAndReload()
      })
  }

  const generateErroredSalesFile = useCallback(() => {
    const retailerSettings = retailerSettingsManager.get()
    const registerData = {
      clientName: WEB_REGISTER.APPLICATION_NAME,
      clientVersion: WEB_REGISTER.CLIENT_VERSION,
      domainPrefix: retailerSettings.domain_prefix,
      retailerId: retailerSettings.id,
      userId: userManager.getLoggedInUser().id,
    }

    saleSyncManager
      .generateErroredSalesFile(registerData)
      .then(fileUrl => setErroredSalesFileUrl(fileUrl))
  }, [retailerSettingsManager, userManager, saleSyncManager])

  // Get the errored sales when the component is mounted
  useEffect(() => {
    const fetchErroredSales = async () => {
      const erroredSales = await saleSyncManager.getErroredSales()
      setErroredSales(orderBy(erroredSales, ['saleDate'], ['desc']))
    }
    // This is to fetch the sales as the page loads
    fetchErroredSales()
    // This will trigger the fetchErroredSales function when the event is
    // emitted which might be happening in the background
    saleSyncManager.on('saleSyncRetryComplete', fetchErroredSales)

    return () => {
      saleSyncManager.off('saleSyncRetryComplete', fetchErroredSales)
      saleSyncManager.clearErroredSalesFile()
    }
  }, [saleSyncManager])

  // Generate the file on component mount
  useEffect(() => {
    generateErroredSalesFile()
  }, [generateErroredSalesFile])

  // Get the outlet timezone as sale dates are displayed in outlet timezone
  useEffect(() => {
    // Should check for `erroredSales` as well?
    if (outletId) {
      registerManager.getOutletById(outletId).then(outlet => {
        // Outlet can be undefined
        if (outlet) {
          setOutletTimeZone(outlet?.time_zone)
        }
      })
    }
  }, [registerManager, outletId])

  return (
    <>
      <PageHeader>{t('Status')}</PageHeader>
      {erroredSales.length > 0 && (
        <ErrorSales
          sales={erroredSales}
          syncInProgress={saleSyncManager.saleSyncRetryInProgress}
          fileUrl={erroredSalesFileUrl}
          timeZone={outletTimeZone}
        />
      )}
      <Section>
        <Header level="4" type="sub-heading">
          {t('Reset local data')}
        </Header>
        <Text tag="p" className="vd-mb5">
          {t(
            "We keep a copy of some of your store data in your web browser so you can keep selling if you lose your Internet connection. Sometimes, this gets out of sync. Resetting it can help if you're having trouble with Lightspeed Retail."
          )}
        </Text>
        {hasAccessToReset() ? (
          <ResetData
            onConfirm={resetData}
            disabled={saleSyncManager.saleSyncRetryInProgress}
          />
        ) : (
          <NoPermissionsPage
            copy={t(
              'You don’t have permission to access the register status page. Switch to a user with permission.'
            )}
          />
        )}
      </Section>
    </>
  )
}

export const StatusViewComponent = reactular(StatusView, [], 'reactWrapper')
